import {
  SubmitButton,
  VendorLocalCancelButton,
} from '@components/FormComponents';
import { TitleTypography } from '../styles';
import {
  OrderSummaryCard,
  OrderSummaryContainer,
  OrderSummaryDataContainer,
  OrderSummaryDetailContainer,
  SubtotalTypography,
  TotalTypography,
  DetailTypography,
  TotalContainer,
  HorizontalContainer,
  DiscountSpan,
} from './OrderSummary.styles';
import { CircularProgress, Skeleton } from '@mui/material';
import { useOrderState } from '@hooks/useOrderState';
import { NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { useCallback, useMemo } from 'react';
import { usePaymentSelectors } from '../paymentMethod/zustand/paymentMethodStore';
import { useLocalPaymentOptionsActions } from '../paymentMethod/zustand/localPaymentOptionsStore';
import {
  CancelPayOrderLocal,
  CancelPayOrderLocalMutationParams,
  CancelPayOrderLocalMutationResponse,
} from '@queries/payment/mutations/cancelPayOrderLocal';
import { useMutation } from '@apollo/client';
import { getRoute } from '@utils';
import { useRouter } from 'next/router';
import { useEventTracking } from '@hooks/useEventTracking';
import { useOpenReplayTracking } from '@services/openreplay';
import { ErrorEventTypes } from '@validators/Events/Error';

const currencyFormatter = new CurrencyFormatter({
  fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
});

const zeroCurrencyFormatter = new CurrencyFormatter({
  fallback: '$0',
});

interface OrderSummaryProps {
  isDelivery: boolean;
  deliveryCost: number;
  oldDeliveryCost?: number;
  cardData?: string;
  handleNext?: () => void;
  buttonText?: string;
  variant?: 'desktop' | 'simple' | 'fixed-bottom';
  showDelivery?: boolean;
  showInsurance?: boolean;
  showMekiCredits?: boolean;
  loading?: boolean;
}

export const OrderSummary = ({
  isDelivery,
  deliveryCost,
  oldDeliveryCost,
  cardData,
  handleNext,
  buttonText = 'Siguiente',
  variant = 'desktop',
  showDelivery = true,
  showInsurance = true,
  showMekiCredits = true,
  loading: parentLoading,
}: OrderSummaryProps) => {
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const { appUser: user } = useAuth();
  const router = useRouter();
  const { track } = useEventTracking();
  const { trackIssue } = useOpenReplayTracking();

  const { order: currentOrder, loadingCurrentOrder } = useOrderState();
  const { installments } = currentOrder?.metadata || {};

  const {
    mekiCreditsChecked,
    insuranceId,
    totalReimbursement,
    totalDeductible,
    totalCopay,
  } = usePaymentSelectors();

  const [cancelPayOrderLocal, { loading: cancelPayOrderLocalLoading }] =
    useMutation<
      CancelPayOrderLocalMutationResponse,
      CancelPayOrderLocalMutationParams
    >(CancelPayOrderLocal());
  const { cleanValues: cleanValuesLocal } = useLocalPaymentOptionsActions();
  const handleCancelPayOrderLocal = useCallback(async () => {
    void cleanValuesLocal();
    await cancelPayOrderLocal({
      variables: {
        params: {
          orderUuid: currentOrder?.uuid,
        },
      },
    })
      .catch((e: Error) => {
        void trackIssue(
          '[CANCEL_PAY_ORDER_LOCAL]: Error cancelling pay order local',
          {
            orderUuid: currentOrder?.uuid,
          },
        );
        void track(ErrorEventTypes.PaymentCancel, {
          type: 'LOCAL',
          error: e,
        });
        throw e;
      })
      .finally(() => {
        void router.push(getRoute('checkoutPaymentMethodLocal'));
      });

    void router.push(getRoute('checkoutPaymentMethodLocal'));
  }, [
    cancelPayOrderLocal,
    cleanValuesLocal,
    currentOrder?.uuid,
    trackIssue,
    router,
    track,
  ]);

  const sellDeductible = insuranceId ? totalCopay - totalDeductible : 0;

  const productsSubTotal = useMemo(() => {
    return currentOrder?.orderDetails.reduce(
      (acc, curr) =>
        acc +
        (curr.price ?? curr.product.discount?.price ?? curr.product.price) *
          curr.quantity,
      0,
    );
  }, [currentOrder?.orderDetails]);

  const productsQuantity = useMemo(() => {
    return currentOrder?.orderDetails.reduce(
      (acc, curr) => acc + curr.quantity,
      0,
    );
  }, [currentOrder?.orderDetails]);

  const userMekiCredits = user?.credits ?? 0;
  const { usedMekiCredits, totalFinal } = useMemo(() => {
    const subTotal = productsSubTotal + (showDelivery ? deliveryCost : 0);
    const usedMekiCredits =
      showMekiCredits && mekiCreditsChecked
        ? Math.min(subTotal, userMekiCredits)
        : 0;
    const insuranceTotalReimbursement =
      showInsurance && Boolean(insuranceId) ? totalReimbursement : 0;
    return {
      usedMekiCredits,
      totalFinal: subTotal - insuranceTotalReimbursement - usedMekiCredits,
    };
  }, [
    productsSubTotal,
    deliveryCost,
    totalReimbursement,
    totalDeductible,
    userMekiCredits,
    mekiCreditsChecked,
    insuranceId,
    showInsurance,
    showMekiCredits,
    showDelivery,
  ]);

  const loading = loadingCurrentOrder || parentLoading;
  return (
    <OrderSummaryCard variant={variant}>
      <OrderSummaryContainer>
        {variant === 'desktop' && (
          <TitleTypography>Resumen compra</TitleTypography>
        )}
        {loading ? (
          <OrderSummarySkeleton variant={variant} />
        ) : (
          <OrderSummaryDataContainer variant={variant}>
            <OrderSummaryDetailContainer>
              <HorizontalContainer>
                <SubtotalTypography>
                  Productos ({productsQuantity})
                </SubtotalTypography>
                <SubtotalTypography>
                  {currencyFormatter.format(productsSubTotal)}
                </SubtotalTypography>
              </HorizontalContainer>
              {showInsurance &&
                Boolean(insuranceId) &&
                (totalReimbursement > 0 ||
                  totalDeductible > 0 ||
                  totalCopay > 0) && (
                  <>
                    <HorizontalContainer>
                      <DetailTypography>Reembolso póliza</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(
                          totalReimbursement + totalDeductible,
                        )}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography>Deducible póliza</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(totalDeductible)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography bold>Aporte seguro</DetailTypography>
                      <DetailTypography bold>
                        {zeroCurrencyFormatter.format(totalReimbursement)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography>Deducible venta</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(sellDeductible)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography bold>
                        Valor a pagar productos
                      </DetailTypography>
                      <DetailTypography bold>
                        {zeroCurrencyFormatter.format(
                          productsSubTotal - totalReimbursement,
                        )}
                      </DetailTypography>
                    </HorizontalContainer>
                  </>
                )}
              {showDelivery && (
                <>
                  {isDelivery ? (
                    <HorizontalContainer>
                      <DetailTypography>Despacho</DetailTypography>
                      <DetailTypography>
                        <DiscountSpan>
                          {oldDeliveryCost && oldDeliveryCost > deliveryCost
                            ? currencyFormatter.format(oldDeliveryCost)
                            : null}
                        </DiscountSpan>
                        {deliveryCost > 0
                          ? currencyFormatter.format(deliveryCost)
                          : 'Gratis'}
                      </DetailTypography>
                    </HorizontalContainer>
                  ) : (
                    <HorizontalContainer>
                      <DetailTypography>Retiro en tienda</DetailTypography>
                      <DetailTypography>Gratis</DetailTypography>
                    </HorizontalContainer>
                  )}
                </>
              )}
              {!isLocalVendor && mekiCreditsChecked && showMekiCredits && (
                <HorizontalContainer>
                  <DetailTypography>Créditos Meki</DetailTypography>
                  <DetailTypography>
                    -{zeroCurrencyFormatter.format(usedMekiCredits)}
                  </DetailTypography>
                </HorizontalContainer>
              )}
            </OrderSummaryDetailContainer>
            {variant !== 'fixed-bottom' && (
              <TotalContainer>
                <HorizontalContainer>
                  <TotalTypography>Total a pagar</TotalTypography>
                  <TotalTypography>
                    {zeroCurrencyFormatter.format(totalFinal)}
                  </TotalTypography>
                </HorizontalContainer>
                {installments > 0 && (
                  <HorizontalContainer>
                    <DetailTypography bold>Nº de cuotas</DetailTypography>
                    <DetailTypography bold>
                      {installments} cuotas
                    </DetailTypography>
                  </HorizontalContainer>
                )}
                {cardData && (
                  <HorizontalContainer>
                    <DetailTypography></DetailTypography>
                    <DetailTypography>{cardData}</DetailTypography>
                  </HorizontalContainer>
                )}
              </TotalContainer>
            )}
          </OrderSummaryDataContainer>
        )}
      </OrderSummaryContainer>
      {handleNext && (
        <SubmitButton disabled={loading} onClick={handleNext}>
          {buttonText}
        </SubmitButton>
      )}
      {isLocalVendor && currentOrder?.status === 'PAYMENT_VALIDATION' && (
        <VendorLocalCancelButton
          disabled={cancelPayOrderLocalLoading}
          onClick={handleCancelPayOrderLocal}
          variant="outlined"
        >
          {cancelPayOrderLocalLoading ? (
            <CircularProgress size={20} />
          ) : (
            'Cancelar'
          )}
        </VendorLocalCancelButton>
      )}
    </OrderSummaryCard>
  );
};

const OrderSummarySkeleton = ({
  variant,
}: {
  variant: 'desktop' | 'simple' | 'fixed-bottom';
}) => (
  <OrderSummaryDataContainer variant={variant}>
    <OrderSummaryDetailContainer>
      <HorizontalContainer>
        <Skeleton variant="rounded" width={160} height={20} />
        <Skeleton variant="rounded" width={80} height={20} />
      </HorizontalContainer>
      <HorizontalContainer>
        <Skeleton variant="rounded" width={160} height={18} />
        <Skeleton variant="rounded" width={80} height={20} />
      </HorizontalContainer>
    </OrderSummaryDetailContainer>
    {variant !== 'fixed-bottom' && (
      <TotalContainer>
        <HorizontalContainer>
          <Skeleton variant="rounded" width={160} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </HorizontalContainer>
      </TotalContainer>
    )}
  </OrderSummaryDataContainer>
);
